import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { manhwaList } from '@/mocks/manhwa';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manhwa = manhwaList.find((m) => m.id === Number(id));

  if (!manhwa) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-book-2-line text-6xl text-foreground-300"></i>
            <h1 className="mt-4 text-xl font-heading font-bold text-foreground-800">
              未找到该漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              该漫画可能已下架或不存在
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedManhwa = manhwaList
    .filter((m) => m.category === manhwa.category && m.id !== manhwa.id)
    .slice(0, 4);

  const mockChapters = Array.from({ length: Math.min(manhwa.chapters, 10) }, (_, i) => ({
    num: manhwa.chapters - i,
    title: `第${manhwa.chapters - i}话`,
    date: new Date(2026, 7 - Math.floor(i / 4), 4 - (i % 4)).toISOString().split('T')[0],
  }));

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link
                to={`/category/${manhwa.category}`}
                className="hover:text-primary-500 transition-colors cursor-pointer"
              >
                {manhwa.category === 'romance' ? '浪漫爱情' : manhwa.category === 'action' ? '热血动作' : manhwa.category === 'fantasy' ? '奇幻冒险' : manhwa.category === 'thriller' ? '悬疑惊悚' : manhwa.category === 'comedy' ? '搞笑日常' : manhwa.category === 'drama' ? '剧情人生' : 'BL专区'}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{manhwa.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manhwa.cover}
                    alt={manhwa.title}
                    className="w-full h-full object-cover"
                    title={manhwa.title}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                  {manhwa.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{manhwa.altTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">
                    <strong>作者：</strong>{manhwa.author}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    manhwa.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {manhwa.status}
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-lg font-bold text-foreground-900">{manhwa.rating}</span>
                    <span className="text-xs text-foreground-500">/ 10</span>
                  </div>
                  <div className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {manhwa.views} 阅读
                  </div>
                  <div className="text-sm text-foreground-500">
                    <i className="ri-file-list-line mr-1"></i>
                    共 {manhwa.chapters} 话
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {manhwa.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {manhwa.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-line mr-2"></i>
                    开始阅读第一话
                  </button>
                  <button className="px-6 py-2.5 bg-foreground-900 hover:bg-foreground-800 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line mr-2"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                章节列表
              </h2>
              <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                {mockChapters.map((ch, index) => (
                  <div
                    key={ch.num}
                    className={`flex items-center justify-between px-5 py-3.5 hover:bg-background-50 transition-colors cursor-pointer ${
                      index < mockChapters.length - 1 ? 'border-b border-background-200/50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-foreground-400 w-8">
                        {String(ch.num).padStart(2, '0')}
                      </span>
                      <span className="text-sm text-foreground-800 font-medium">
                        {ch.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-500">{ch.date}</span>
                  </div>
                ))}
                {manhwa.chapters > 10 && (
                  <div className="px-5 py-4 text-center">
                    <button className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                      查看全部 {manhwa.chapters} 话
                      <i className="ri-arrow-down-s-line ml-1"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {relatedManhwa.length > 0 && (
              <div className="lg:w-72 flex-shrink-0">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  同类推荐
                </h2>
                <div className="space-y-3">
                  {relatedManhwa.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="flex items-center gap-3 p-3 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                    >
                      <div className="w-14 h-[72px] rounded overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={related.cover}
                          alt={related.title}
                          className="w-full h-full object-cover"
                          title={related.title}
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-1">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          <span className="text-xs text-foreground-600">{related.rating}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: manhwa.title,
            alternativeHeadline: manhwa.altTitle,
            author: {
              '@type': 'Person',
              name: manhwa.author,
            },
            description: manhwa.description,
            datePublished: manhwa.updateTime,
            url: `https://www.xkmanhua.com/article/${manhwa.id}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '虚空韩漫网',
              url: 'https://www.xkmanhua.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.xkmanhua.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: manhwa.category,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: manhwa.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}